<template lang="pug">
  content-page
    h1(v-html="contentpage.name")
    ws-content(:value="contentpage.content" )
      snowheight-list-table(:snowheights="snowheightsForCountry")
    div(slot="nav")
      navigation
      ad
</template>

<script>
import gql from 'graphql-tag';
import { interpolateFields } from '@/utils/interpolate.js';
import Navigation from '@/components/navigation';
import SnowheightListTable from '@/components/weather/snowheight-list-table';
import MetaFragment from '@/components/graphql/metaFragment.graphql';
import Content from '@dengl/common/components/content.vue';

const query = gql`
	query getCountrySnowheight($slug: String!, $path: String) {
		contentpage(path: $path) {
			id
			name
			slug
			content {
				...ContentDataFragment
			}
			...MetaFragment
		}
		areas {
			id
			name
			slug
			country {
				id
				name
				countryCode
			}
			snowheights {
				maxSnowValley
				maxSnowMountain
				nodes {
					lastSnowAt
					snowLast24
					slopeLengthOpen
				}
			}
		}
		country(slug: $slug) {
			name
			id
			countryCode
		}
	}
	${MetaFragment}
	${Content.fragments}
`;

export default {
	name: 'WeatherSnowheightCountry',
	components: {
		Navigation,
		SnowheightListTable
	},
	layout: 'default',
	nuxtI18n: {
		paths: {
			'nl-NL': '/weersneeuw/sneeuwhoogte/:country',
			'nl-BE': '/weersneeuw/sneeuwhoogte/:country',
			'de-DE': '/wetter-schnee/schneehoehe/:country'
		}
	},
	asyncData({ params, app, route }) {
		return app.$graphClient
			.query({
				query,
				variables: {
					slug: params.country,
					path: route.path.replace(params.country, '_country')
				},
				errorPolicy: 'all'
			})
			.then(({ data, errors }) => {
				if (errors) {
					throw errors[0];
				}

				const interPolateFunction = interpolateFields({
					find: /{(country)}/gi,
					replace: data.country.name
				});
				interPolateFunction(data.contentpage, ['name']);
				interPolateFunction(data.contentpage.content, ['markdown']);
				interPolateFunction(data.contentpage.meta, ['title', 'description']);

				app.ads({
					navigation: { visible: true, targets: { country: data.country.id } }
				});
				app.meta(data.contentpage.meta);
				app.breadcrumbs({
					[app.i18n.t('page.weather:breadcrumb')]: { name: 'weather' },
					[app.i18n.t('page.weather.snowheight:heading')]: {
						name: 'weather-snowheight'
					},
					[data.country.name]: {
						name: 'weather-snowheight-country',
						params: { country: params.country }
					}
				});

				return data;
			})
			.catch(error => {
				return app.httpError(
					500,
					null,
					'weather-snowheights-country-01',
					error
				);
			});
	},
	head() {
		return {
			...this.$structuredData()
		};
	},
	computed: {
		snowheightsForCountry() {
			let nodes = this.areas;
			// remove items without snow
			let countryCode = this.country.countryCode;
			if (countryCode) {
				countryCode = Array.isArray(countryCode) ? countryCode : [countryCode];
				nodes = nodes.filter(node =>
					countryCode.includes(node.country.countryCode)
				);
			}
			nodes = nodes.filter(
				node =>
					node.snowheights.maxSnowMountain > 0 ||
					node.snowheights.maxSnowValley > 0
			);

			// sort by maxSnow
			nodes.sort(function (a, b) {
				return b.snowheights.maxSnowMountain - a.snowheights.maxSnowMountain;
			});

			// create flat object
			nodes = nodes.map((node, index) => {
				// get ranking with classes
				const rankColors = ['gold', 'silver', 'bronze'];
				const ranking = index + 1;
				const rankClass = ranking - 1 > 2 ? ['black--text'] : ['white--text'];
				rankClass.push(rankColors[ranking - 1] || 'grey lighten-3');

				// find snowLast24
				let snowheights = [...node.snowheights.nodes];
				const snowLast24 = snowheights.length
					? snowheights.reduce((prev, curr) => {
							return prev.snowLast24 < curr.snowLast24 ? prev : curr;
					  }).snowLast24
					: null;

				// find snowLast24
				snowheights = [...node.snowheights.nodes];
				const lastSnowAt = snowheights.length
					? snowheights.reduce((prev, curr) => {
							return this.$dayjs(prev.lastSnowAt).isBefore(
								this.$dayjs(curr.lastSnowAt)
							)
								? prev
								: curr;
					  }).lastSnowAt
					: null;

				// find slopeLengthOpen
				snowheights = [...node.snowheights.nodes];
				const slopeLengthOpen = snowheights.length
					? snowheights.reduce((prev, curr) => {
							return prev.slopeLengthOpen < curr.slopeLengthOpen ? prev : curr;
					  }).slopeLengthOpen
					: 0;

				return {
					id: node.id,
					name: node.name,
					slug: node.slug,
					country: node.country,
					ranking,
					rankClass,
					snowMountain: node.snowheights.maxSnowMountain,
					snowValley: node.snowheights.maxSnowValley,
					snowLast24,
					lastSnowAt,
					slopeLengthOpen
				};
			});

			return nodes;
		}
	}
};
</script>
